import type { TFunction } from "i18next";

import type { RxTreatmentOverview } from "./treatmentOverviewGuards";

/**
 * Extended-release Metformin can be prescribed once or twice daily, but the
 * schedule model only supports one exact frequency. Hardcoded until the
 * backend can represent a frequency range.
 */
const METFORMIN_TREATMENT_CODE = "ORAL_METFORMIN";

/**
 * NAD dosing is every 3 days, a cadence the schedule model can't express yet
 * (only DAY/WEEK). Hardcoded for launch so the copy is right whatever cadence
 * the catalog sends; remove once the backend can represent day intervals.
 */
const NAD_TREATMENT_CODE = "NAD";

/**
 * A new intake that no clinician has prescribed yet — incomplete intake or
 * first review. Refills of both statuses already have a prescription behind
 * them, matching the refill split used for status rows and detail cards.
 */
const isAwaitingFirstPrescription = (
  treatment: RxTreatmentOverview
): boolean => {
  const statusTypename = treatment.status?.__typename;

  if (
    statusTypename !== "TreatmentOverviewStatusIntakeIncomplete" &&
    statusTypename !== "TreatmentOverviewStatusInReview"
  ) {
    return false;
  }

  return !treatment.isRefill;
};

export const formatRxSchedule = (
  treatment: RxTreatmentOverview,
  t: TFunction<"care">
): string | undefined => {
  const { schedule, scheduledDayOfWeek, treatmentCode } = treatment;
  const normalizedTreatmentCode = treatmentCode?.toUpperCase();

  if (normalizedTreatmentCode === METFORMIN_TREATMENT_CODE) {
    return isAwaitingFirstPrescription(treatment)
      ? undefined
      : t("schedule.oneToTwoTimesDaily");
  }

  if (normalizedTreatmentCode === NAD_TREATMENT_CODE) {
    return isAwaitingFirstPrescription(treatment)
      ? undefined
      : t("schedule.everyThreeDays");
  }

  if (!schedule) {
    return undefined;
  }

  if (schedule.cadence === "WEEK" && scheduledDayOfWeek) {
    return t("schedule.weeklyOnDayTemplate", {
      day: t(`daysOfWeek.${scheduledDayOfWeek}`),
    });
  }

  if (schedule.cadence === "WEEK" && schedule.frequency > 1) {
    return t("schedule.timesPerWeekTemplate", { count: schedule.frequency });
  }

  if (schedule.cadence === "WEEK") {
    return t("schedule.weekly");
  }

  if (schedule.cadence === "DAY") {
    return t("schedule.daily");
  }

  return undefined;
};
